/**
 * BuildingInfoPanel
 *
 * Premium dark overlay panel showing building details.
 * Displays unlock requirements for locked buildings and enter button for unlocked ones.
 * Uses the building's semantic color as header accent.
 */

import React from 'react';
import { Building, Lock, ArrowRight, MapPin } from 'lucide-react';
import { LocationConfig, UnlockRequirement } from '../../../types/spatial';
import { getAdjacentLocations, getLocationConfig } from '../../../config/locationConfig';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

export interface BuildingInfoPanelProps {
  config: LocationConfig;
  isUnlocked: boolean;
  onEnter: () => void;
}

const formatRequirementValue = (req: UnlockRequirement): string => {
  switch (req.type) {
    case 'cash':
      return formatCurrency(req.value);
    case 'products':
      return `${req.value} product${req.value > 1 ? 's' : ''}`;
    case 'employees':
      return `${req.value} employee${req.value > 1 ? 's' : ''}`;
    case 'level':
      return `Level ${req.value}`;
    case 'research':
      return `${req.value} research point${req.value > 1 ? 's' : ''}`;
    default:
      return `${req.value}`;
  }
};

export const BuildingInfoPanel: React.FC<BuildingInfoPanelProps> = ({
  config,
  isUnlocked,
  onEnter,
}) => {
  const adjacentLocations = getAdjacentLocations(config.id);

  return (
    <div
      className="absolute bottom-4 right-4 w-80 max-w-[calc(100%-2rem)] max-h-[calc(100%-6rem)]
                 bg-gray-900/90 backdrop-blur-md border border-white/10 rounded-lg
                 shadow-xl overflow-y-auto animate-slideInRight"
      data-testid="building-info-panel"
    >
      {/* Header with building color accent */}
      <div
        className="px-4 py-3 border-b border-white/10"
        style={{ backgroundColor: isUnlocked ? config.color : '#4b5563' }}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            {isUnlocked ? (
              <Building className="w-5 h-5 text-white" />
            ) : (
              <Lock className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white font-display">{config.name}</h3>
            {config.name !== config.buildingName && (
              <p className="text-sm text-white/70">{config.buildingName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <p className="text-sm text-white/60">{config.description}</p>

        {/* Unlock Requirements */}
        {!isUnlocked && config.unlockRequirements && config.unlockRequirements.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-white flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" />
              Requirements to Unlock
            </h4>
            <ul className="space-y-1.5">
              {config.unlockRequirements.map((req, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-white/60"
                >
                  <span className="text-amber-400 mt-0.5">-</span>
                  <span>
                    {req.description} ({formatRequirementValue(req)})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Adjacent Locations */}
        {adjacentLocations.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-400" />
              Nearby Buildings
            </h4>
            <div className="flex flex-wrap gap-2">
              {adjacentLocations.map((loc) => {
                const locConfig = getLocationConfig(loc);
                return (
                  <span
                    key={loc}
                    className="px-2 py-1 text-xs bg-white/10 rounded-full
                               text-white/70 border border-white/10"
                  >
                    {locConfig.buildingName}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="px-4 pb-4">
        {isUnlocked ? (
          <button
            onClick={onEnter}
            className="w-full flex items-center justify-center gap-2 px-4 py-3
                       bg-emerald-500 hover:bg-emerald-400 text-white
                       rounded-lg font-medium transition-colors"
          >
            Enter {config.buildingName}
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <div
            className="w-full flex items-center justify-center gap-2 px-4 py-3
                        bg-white/5 text-white/40 rounded-lg
                        font-medium cursor-not-allowed border border-white/10"
          >
            <Lock className="w-4 h-4" />
            Building Locked
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildingInfoPanel;
